import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const DIET_LABELS = {
  standard:   'Aucun',
  vegetarian: 'Végétarien',
  vegan:      'Végan',
  halal:      'Halal',
  kid:        'Menu enfant',
  other:      'Autre',
};

export default function GuestModal({ open, onClose, guest, groups, mainSeg, onSave }) {
  const isEdit = !!guest;

  const [nom,     setNom]     = useState('');
  const [email,   setEmail]   = useState('');
  const [phone,   setPhone]   = useState('');
  const [groupId, setGroupId] = useState(null);
  const [diet,    setDiet]    = useState('standard');
  const [age,     setAge]     = useState('adult');
  const [note,    setNote]    = useState('');
  const [hasPlus, setHasPlus] = useState(false);
  const [plusNom, setPlusNom] = useState('');
  const [plusDiet,setPlusDiet]= useState('standard');
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);

  // Pré-remplissage en mode édition
  useEffect(() => {
    if (!open) return;
    if (guest) {
      setNom(guest.nom ?? '');
      setEmail(guest.email ?? '');
      setPhone(guest.telephone ?? '');
      setGroupId(guest.group_id ?? null);
      setDiet(guest.diet ?? 'standard');
      setAge(guest.age ?? 'adult');
      setNote(guest.note ?? '');
      const po = guest.plus_one;
      setHasPlus(!!po?.name);
      setPlusNom(po?.name ?? '');
      setPlusDiet(po?.diet ?? 'standard');
    } else {
      setNom(''); setEmail(''); setPhone('');
      setGroupId(groups?.[0]?.id ?? null);
      setDiet('standard'); setAge('adult'); setNote('');
      setHasPlus(false); setPlusNom(''); setPlusDiet('standard');
    }
    setErrors({});
  }, [open, guest, groups]);

  function validate() {
    const errs = {};
    if (!nom.trim()) errs.nom = 'Le nom est requis';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Email invalide';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const data = {
      nom: nom.trim(), email: email || null, telephone: phone,
      group_id: groupId, diet, age, note,
      plus_one: hasPlus && plusNom.trim()
        ? { name: plusNom.trim(), diet: plusDiet }
        : null,
    };
    try {
      await onSave(data);
      toast.success(isEdit ? 'Invité modifié' : 'Invité ajouté');
      onClose();
    } catch (err) {
      toast.error('Erreur : ' + (err.message ?? 'Inconnue'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent style={{ maxWidth: 520, fontFamily: "'Montserrat',sans-serif" }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>
            {isEdit ? 'Modifier l\'invité' : 'Ajouter un invité'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>

          {/* Nom */}
          <div>
            <label style={labelStyle}>Nom *</label>
            <input autoFocus value={nom} onChange={e=>setNom(e.target.value)}
              style={{ ...inputStyle, borderColor: errors.nom ? '#EF4444' : '#D1D5DB' }}
              placeholder="Prénom NOM" />
            {errors.nom && <p style={errStyle}>{errors.nom}</p>}
          </div>

          {/* Email + Tél */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={email} onChange={e=>setEmail(e.target.value)}
                type="email" placeholder="email@exemple.fr"
                style={{ ...inputStyle, borderColor: errors.email ? '#EF4444' : '#D1D5DB' }} />
              {errors.email && <p style={errStyle}>{errors.email}</p>}
            </div>
            <div>
              <label style={labelStyle}>Téléphone</label>
              <input value={phone} onChange={e=>setPhone(e.target.value)}
                placeholder="06 XX XX XX XX" style={inputStyle} />
            </div>
          </div>

          {/* Groupes */}
          {groups.length > 0 && (
            <div>
              <label style={labelStyle}>Groupe</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:4 }}>
                {groups.map(g => (
                  <button key={g.id} type="button"
                    onClick={() => setGroupId(g.id)}
                    style={{
                      padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600,
                      cursor:'pointer',
                      background: groupId===g.id ? g.color : `${g.color}22`,
                      border: `1.5px solid ${g.color}`,
                      color: groupId===g.id ? '#0B1E20' : '#374151',
                    }}>
                    {g.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Régime + Âge */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={labelStyle}>Régime alimentaire</label>
              <Select value={diet} onValueChange={setDiet}>
                <SelectTrigger style={{ fontSize:12, height:34 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DIET_LABELS).map(([v,l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label style={labelStyle}>Âge</label>
              <RadioGroup value={age} onValueChange={setAge}
                style={{ display:'flex', gap:16, marginTop:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <RadioGroupItem value="adult" id="adult" />
                  <label htmlFor="adult" style={{ fontSize:12, cursor:'pointer' }}>Adulte</label>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <RadioGroupItem value="kid" id="kid" />
                  <label htmlFor="kid" style={{ fontSize:12, cursor:'pointer' }}>Enfant</label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* Note */}
          <div>
            <label style={labelStyle}>Note / Allergie</label>
            <textarea value={note} onChange={e=>setNote(e.target.value)}
              rows={2} placeholder="Allergie noix, chaise haute nécessaire…"
              style={{ ...inputStyle, resize:'none', paddingTop:6, paddingBottom:6 }} />
          </div>

          {/* +1 */}
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <Switch checked={hasPlus} onCheckedChange={setHasPlus} id="plus-one" />
              <label htmlFor="plus-one" style={{ fontSize:12, fontWeight:600, cursor:'pointer' }}>
                Avec un accompagnant (+1)
              </label>
            </div>
            {hasPlus && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, paddingLeft:4 }}>
                <div>
                  <label style={labelStyle}>Nom du +1</label>
                  <input value={plusNom} onChange={e=>setPlusNom(e.target.value)}
                    placeholder="Prénom NOM" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Régime +1</label>
                  <Select value={plusDiet} onValueChange={setPlusDiet}>
                    <SelectTrigger style={{ fontSize:12, height:34 }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(DIET_LABELS).map(([v,l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <DialogFooter style={{ marginTop:4 }}>
            <button type="button" onClick={onClose}
              style={{ ...btnBase, background:'#F3F4F6', color:'#374151' }}>
              Annuler
            </button>
            <button type="submit" disabled={saving}
              style={{ ...btnBase, background:'#D4AF37', color:'#0B1E20', fontWeight:700,
                opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const labelStyle = { display:'block', fontSize:11, fontWeight:600, color:'#374151', marginBottom:3 };
const inputStyle = {
  width:'100%', padding:'6px 9px', borderRadius:6, fontSize:12, boxSizing:'border-box',
  border:'1px solid #D1D5DB', outline:'none', fontFamily:"'Montserrat',sans-serif",
};
const errStyle   = { fontSize:10, color:'#EF4444', marginTop:2 };
const btnBase    = {
  padding:'8px 18px', borderRadius:8, border:'none', fontSize:13,
  cursor:'pointer', fontFamily:"'Montserrat',sans-serif",
};
